import { notifications } from "@mantine/notifications";

/**
 * Error types returned from backend document upload service
 */
export type ApiErrorType =
  | "password_protected" // Initial detection (new from backend)
  | "password_incorrect" // Wrong password attempt (decryption flow)
  | "validation_error" // File validation failed (empty, too large, etc.)
  | "s3_error" // S3 upload failed
  | "db_error"; // Database error

/**
 * API error response structure from backend
 */
export interface ApiErrorResponse {
  error: string;
  errorType: ApiErrorType;
  requiresRetry?: boolean;
}

/**
 * Extracted error information
 */
export interface ExtractedError {
  message: string;
  type: string | null;
}

interface SmartwizErrorProps {
  /**
   * The title of the error message.
   */
  title?: string;
  /**
   * The message in the error.
   */
  message: string;
}

/**
 * Custom error used to control the validation logic for forms through application.
 */
export class SmartwizError extends Error {
  public title?: string;

  constructor({ message, title }: SmartwizErrorProps) {
    super(message);
    this.name = "SmartwizError";
    this.title = title;
    Object.setPrototypeOf(this, SmartwizError.prototype);
  }
}

interface AttemptMessageProps {
  successMessage?: string;
  errorMessage: string;
}

/**
 * Performs function with error handling and notifies user gracefully.
 * @param fn The function to perform and whose result is returned.
 * @param successMessage The message to display is function is executed successfully.
 * @param errorMessage The error message to display is function fails (and Smartwiz error is not caught).
 */
export async function attempt<T>(
  fn: () => Promise<T>,
  { successMessage, errorMessage }: AttemptMessageProps
): Promise<T | undefined> {
  try {
    const result = await fn(); // Execute and await the fn, storing the result
    if (successMessage) {
      notifications.show({
        message: successMessage,
        color: "green",
      });
    }
    return result; // Return the result of fn
  } catch (e) {
    if (e instanceof SmartwizError) {
      showError(e.message, e.title);
    } else {
      showError(errorMessage);
    }
    return undefined; // Return undefined in case of an error
  }
}

/**
 * Central function for showing an error message in smartwiz frontend.
 * @param errorMessage The error message.
 * @param title The title of the message.
 */
export const showError = (errorMessage: string, title?: string) => {
  console.error(errorMessage);
  notifications.show({
    message: errorMessage,
    ...(title && { title }), // include only if defined
    color: "red",
  });
};

/**
 * Extracts error message and type from axios error response
 *
 * Backend returns errors in this structure:
 * ```json
 * {
 *   "message": "File 'document.pdf' is password-protected...",
 *   "content": {
 *     "error": "File 'document.pdf' is password-protected. Please remove the password or provide it.",
 *     "errorType": "password_protected"
 *   }
 * }
 * ```
 *
 * @param error - Axios error object
 * @returns Extracted error message and type
 *
 * @example
 * ```typescript
 * try {
 *   await uploadDocument(file);
 * } catch (err) {
 *   const { message, type } = extractApiError(err);
 *
 *   if (type === "password_protected") {
 *     showError(message, "Password-Protected File");
 *   } else if (type === "validation_error") {
 *     showError(message, "Validation Error");
 *   } else {
 *     showError(message, "Upload Failed");
 *   }
 * }
 * ```
 */
export const extractApiError = (error: any): ExtractedError => {
  // Try to extract from response.data.content (structured error)
  const apiError: ApiErrorResponse | undefined = error?.response?.data?.content;

  if (apiError && apiError.error) {
    return {
      message: apiError.error,
      type: apiError.errorType || null,
    };
  }

  // Fallback to response.data.message (generic error)
  if (error?.response?.data?.message) {
    return {
      message: error.response.data.message,
      type: null,
    };
  }

  // Fallback to error message
  return {
    message: error instanceof Error ? error.message : "An error occurred",
    type: null,
  };
};

/**
 * Gets a user-friendly error title based on error type
 *
 * @param errorType - Error type from backend
 * @returns User-friendly error title
 */
export const getErrorTitle = (errorType: string | null): string => {
  switch (errorType) {
    case "password_protected":
      return "File is Locked";
    case "password_incorrect":
      return "Wrong Password";
    case "validation_error":
      return "Can't Use This File";
    case "s3_error":
      return "Upload Failed";
    case "db_error":
      return "Something Went Wrong";
    default:
      return "Upload Failed";
  }
};

/**
 * Gets a user-friendly error message based on error type
 * Falls back to the provided message if no specific message is available
 *
 * Messages are written at a 3rd grade reading level and focus on how to fix the issue.
 *
 * @param errorType - Error type from backend
 * @param defaultMessage - Default message to show if no specific message exists
 * @returns User-friendly error message
 */
export const getErrorMessage = (
  errorType: string | null,
  defaultMessage: string
): string => {
  switch (errorType) {
    case "password_protected":
      return "This file is locked. Remove the password before uploading.";
    case "password_incorrect":
      return "Wrong password. Try again.";
    case "validation_error":
      // For validation errors, use the backend's specific message (e.g., "file is broken", "file is empty")
      // Check if the message is already simple enough
      if (
        defaultMessage.includes("truncated") ||
        defaultMessage.includes("corrupted") ||
        defaultMessage.includes("EOF") ||
        defaultMessage.includes("broken") ||
        defaultMessage.includes("invalid")
      ) {
        return "Invalid file. Try a different one.";
      } else if (defaultMessage.includes("empty")) {
        return "Empty file. Choose a file with content.";
      } else if (
        defaultMessage.includes("size") ||
        defaultMessage.includes("large") ||
        defaultMessage.includes("big")
      ) {
        return "File too big. Choose a smaller one.";
      }
      return defaultMessage;
    case "s3_error":
      return "Upload failed. Try again.";
    case "db_error":
      return "Couldn't save file. Try again.";
    default:
      return defaultMessage;
  }
};
